import { Box, Button, InputBase, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { isUnusualDoseFactor } from '~/domain/dose/doseFactor';
import { replanningDoseScaleState } from '~/state/atoms/adaptiveReplanningAtoms';
import { readDoseFactor } from './doseFactorEntry';

interface DoseScaleFieldProps {
    disabled?: boolean;
}

export default function DoseScaleField({
    disabled = false,
}: DoseScaleFieldProps) {
    const intl = useIntl();
    const [scale, setScale] = useRecoilState(replanningDoseScaleState);
    const [text, setText] = useState(String(scale));

    useEffect(() => {
        setText(String(scale));
    }, [scale]);

    const { factor, refusal } = readDoseFactor(text);
    const valid = factor !== undefined;
    const changed = valid && factor !== scale;
    const unusual = valid && isUnusualDoseFactor(factor);
    // Said only when there is something to say: a refused or unusual factor.
    const message = refusal
        ? intl.formatMessage({ id: refusal.id }, refusal.values)
        : unusual
          ? intl.formatMessage(
                { id: 'replanning.dose.scale.unusual' },
                { scale: factor }
            )
          : undefined;
    const label = intl.formatMessage({ id: 'replanning.dose.scale.label' });

    const apply = () => {
        if (changed) {
            setScale(factor);
        }
    };

    return (
        <Box>
            <Box display="flex" alignItems="center" gap={1}>
                <Tooltip
                    title={intl.formatMessage({
                        id: 'replanning.dose.scale.help',
                    })}
                >
                    <Typography
                        component="label"
                        htmlFor="replanning-dose-scale"
                        fontSize={11.5}
                        color="text.secondary"
                        flexShrink={0}
                    >
                        {label}
                    </Typography>
                </Tooltip>
                <InputBase
                    id="replanning-dose-scale"
                    value={text}
                    disabled={disabled}
                    onChange={(event) => setText(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            apply();
                        }
                    }}
                    error={!valid}
                    inputProps={{
                        inputMode: 'decimal',
                        'data-testid': 'replanning-dose-scale-input',
                        'aria-invalid': !valid,
                        'aria-describedby': message
                            ? 'replanning-dose-scale-helper'
                            : undefined,
                    }}
                    sx={{
                        width: 72,
                        height: 26,
                        px: 1,
                        fontSize: 12,
                        border: '1px solid',
                        borderColor: !valid
                            ? 'error.main'
                            : unusual
                              ? 'warning.main'
                              : 'divider',
                        borderRadius: 1,
                    }}
                />
                <Button
                    size="small"
                    variant="outlined"
                    onClick={apply}
                    disabled={disabled || !changed}
                    data-testid="replanning-dose-scale-apply"
                    sx={{ height: 26, minWidth: 0, px: 1.25, fontSize: 11 }}
                >
                    <FormattedMessage id="replanning.dose.scale.apply" />
                </Button>
            </Box>
            {message && (
                <Typography
                    id="replanning-dose-scale-helper"
                    fontSize={11}
                    mt={0.5}
                    color={!valid ? 'error.main' : 'warning.main'}
                >
                    {message}
                </Typography>
            )}
        </Box>
    );
}
